import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { UserManageService } from "../../services/UserManageService";
import { UserService } from "../../services/UserService";

export default function BlockUser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [matricula, setMatricula] = useState("");
  const [fullName, setFullName] = useState("");
  const [period, setPeriod] = useState("3");
  const [justification, setJustification] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [blockList, setBlockList] = useState([]);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const userManageService = new UserManageService();
  const userService = new UserService();

  // Get current page from URL or default to 1
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Fetch blocked users on component mount and when page changes
  useEffect(() => {
    fetchBlockedUsers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchBlockedUsers = async (page = 1) => {
    try {
      const response = await userManageService.getAllBlockedUsers(page, 10);
      if (response && response.data) {
        const formattedBlocks = response.data.map((user) => {
          const endDate =
            user.blockedUntilFormatted !== "Permanente"
              ? user.blockedUntilFormatted
              : "Indeterminado";

          return {
            matricula: user.username,
            name: user.fullName,
            justification: user.blockReason,
            startDate: user.blockStartFormatted || "N/A",
            endDate: endDate,
            recordCreated: user.recordCreatedFormatted || "N/A",
            blockedBy: user.blockedBy?.name || "Sistema",
            status: user.isCurrentlyBlocked ? "Ativo" : "Concluído",
            active: user.isCurrentlyBlocked,
            _id: user.blockActivityLogId || user._id,
            blockType: user.blockType,
            blockMethod: user.blockMethod,
            userId: user._id,
          };
        });

        setBlockList(formattedBlocks);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error fetching blocked users:", error);
      setError("Erro ao carregar lista de bloqueios.");
    }
  };

  const handleFetchUser = async () => {
    if (!matricula) return;

    try {
      const user = await userManageService.findByUsername(matricula);
      if (user) {
        setFullName(`${user.name} ${user.surname} ${user.lastName}`.trim());
      } else {
        setFullName("");
        setError("Usuário não encontrado.");
      }
    } catch {
      setFullName("");
      setError("Erro ao buscar usuário.");
    }
  };

  const handleBlockUser = async () => {
    setLoading(true);
    setError("");

    if (!matricula || !period || !justification || !password) {
      setError("Todos os campos são obrigatórios.");
      setLoading(false);
      return;
    }

    try {
      // First verify the admin's password
      const isPasswordValid = await userService.verifyPassword(password);

      if (!isPasswordValid) {
        setError(
          "Senha incorreta. Por favor, verifique sua senha e tente novamente."
        );
        setLoading(false);
        return;
      }

      // If password is valid, proceed with blocking the user
      await userManageService.block(
        matricula,
        justification,
        parseInt(period, 10)
      );

      // Clear form fields after successful block
      setMatricula("");
      setFullName("");
      setPeriod("3");
      setJustification("");
      setPassword("");

      // Refresh block list after blocking (go to page 1)
      setSearchParams({ page: "1" });
      fetchBlockedUsers(1);
    } catch (error) {
      setError(error?.message || "Erro ao bloquear usuário.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ page: newPage.toString() });
  };

  const handleUnblockUser = async (username, blockId) => {
    setLoading(true);
    try {
      await userManageService.unblock(username, blockId);
      fetchBlockedUsers(currentPage); // Refresh list after unblocking
    } catch {
      setError("Erro ao desbloquear usuário.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3">Bloquear</h2>
      <p>
        Informe a matrícula do GCM e o sistema exibirá o nome completo. Depois,
        informe o período de bloqueio (em dias úteis) e a justificativa.
      </p>

      {/* Blocking Form */}
      <div className="d-flex gap-3">
        <div>
          <label>
            Matrícula <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={matricula}
            onChange={(e) => setMatricula(e.target.value)}
            onBlur={handleFetchUser}
            placeholder="Ex: 123456"
            required
          />
        </div>
        <div>
          <label>Nome Completo</label>
          <div className="mt-2">
            <span className="fw-bold">{fullName || "Digite a matrícula"}</span>
          </div>
        </div>
        <div>
          <label>
            Período (dias) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className="form-control"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            min="1"
            required
          />
        </div>
        <div>
          <label>
            Justificativa <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Motivo do bloqueio"
            required
          />
        </div>
        <div>
          <label>
            Sua Senha <span className="text-danger">*</span>
          </label>
          <input
            type="password"
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Digite sua senha para confirmar"
            required
          />
        </div>
        <div className="d-flex align-items-end">
          <button
            className="btn btn-primary"
            onClick={handleBlockUser}
            disabled={loading}
          >
            {loading ? "Bloqueando..." : "Bloquear"}
          </button>
        </div>
      </div>

      {error && <div className="text-danger mt-2">{error}</div>}

      {/* Block List */}
      <div className="mt-5 p-3 bg-light rounded">
        <h4>Histórico de Bloqueios</h4>
        <p>
          Confira abaixo o histórico completo de bloqueios no sistema (ativos e
          concluídos).
        </p>

        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Matrícula</th>
                <th>Nome Completo</th>
                <th>Tipo</th>
                <th>Método</th>
                <th>Justificativa</th>
                <th>Data de Registro</th>
                <th>Início</th>
                <th>Término</th>
                <th>Bloqueado por</th>
                <th>Status</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {blockList.map((block, index) => (
                <tr
                  key={index}
                  className={block.active ? "" : "table-secondary"}
                >
                  <td className="fw-bold">{block.matricula}</td>
                  <td>{block.name}</td>
                  <td>
                    <span
                      className={`badge ${
                        block.blockType === "Permanente"
                          ? "bg-danger"
                          : "bg-warning text-dark"
                      }`}
                    >
                      {block.blockType}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        block.blockMethod === "Manual"
                          ? "bg-info"
                          : block.blockMethod?.includes("Cancelamento")
                          ? "bg-warning text-dark"
                          : "bg-secondary"
                      }`}
                    >
                      {block.blockMethod}
                    </span>
                  </td>
                  <td title={block.justification}>
                    {block.justification.length > 30
                      ? block.justification.substring(0, 30) + "..."
                      : block.justification}
                  </td>
                  <td className="text-nowrap">
                    <small className="text-muted">
                      {block.recordCreated || "N/A"}
                    </small>
                  </td>
                  <td className="text-nowrap">
                    <small>{block.startDate || "N/A"}</small>
                  </td>
                  <td className="text-nowrap">
                    {block.blockType === "Permanente" ? (
                      <span className="text-danger fw-bold">Indeterminado</span>
                    ) : (
                      <small>{block.endDate || "N/A"}</small>
                    )}
                  </td>
                  <td>{block.blockedBy}</td>
                  <td>
                    <span
                      className={`badge ${
                        block.active
                          ? "bg-danger"
                          : block.status === "Expirado"
                          ? "bg-secondary"
                          : "bg-success"
                      }`}
                    >
                      {block.active
                        ? "Bloqueado"
                        : block.status || "Desbloqueado"}
                    </span>
                  </td>
                  <td>
                    {block.active && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          handleUnblockUser(block.matricula, block._id)
                        }
                        disabled={loading}
                      >
                        {loading ? "..." : "Desbloquear"}
                      </button>
                    )}
                    {!block.active && block.status && (
                      <span className="text-muted">
                        {block.status === "Expirado"
                          ? "Bloqueio expirado"
                          : "Já desbloqueado"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {blockList.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">
              Nenhum histórico de bloqueio encontrado.
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <nav aria-label="Navegação de páginas">
            <ul className="pagination justify-content-center mt-4">
              <li
                className={`page-item ${
                  pagination.currentPage === 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  Anterior
                </button>
              </li>

              {/* First page */}
              {pagination.currentPage > 3 && (
                <>
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                  </li>
                  {pagination.currentPage > 4 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                </>
              )}

              {/* Page numbers */}
              {[...Array(pagination.totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show current page, and 2 pages before and after
                if (
                  pageNum === pagination.currentPage ||
                  (pageNum >= pagination.currentPage - 2 &&
                    pageNum <= pagination.currentPage + 2)
                ) {
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        pageNum === pagination.currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                }
                return null;
              })}

              {/* Last page */}
              {pagination.currentPage < pagination.totalPages - 2 && (
                <>
                  {pagination.currentPage < pagination.totalPages - 3 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.totalPages)}
                    >
                      {pagination.totalPages}
                    </button>
                  </li>
                </>
              )}

              <li
                className={`page-item ${
                  pagination.currentPage === pagination.totalPages
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  Próximo
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
}
